import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Avatar3D, { Avatar3DRef } from '../components/Avatar3D';
import VoiceRecorder from '../components/VoiceRecorder';
import AIService from '../services/AIService';
import AuthService from '../services/AuthService';
import { useAuthExpiration } from '../hooks/useAuthExpiration';

interface DashboardProps {
  onLogout: () => void;
}

const DashboardContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  height: calc(100vh - 60px);
  overflow: hidden;
`;

const LeftPanel = styled.div`
  width: 450px;
  height: 100%;
  padding: 30px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-right: 2px solid rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  z-index: 1000;
  pointer-events: auto;
  flex-shrink: 0;
`;

const CenterPanel = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  background-image: url('/BackgroundDHL.png');
  background-size: 100% 100%;
  background-position: center -50px;
  background-repeat: no-repeat;
`;

const RightPanel = styled.div`
  width: 200px;
  height: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #DC143C 0%, #B22222 50%, #8B0000 100%);
  backdrop-filter: blur(10px);
  border-left: 2px solid rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const StatusTitle = styled.h3`
  color: #333;
  margin: 0 0 20px 0;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: left;
`;

const StatusItem = styled.div`
  margin-bottom: 15px;
  flex-shrink: 0;
`;

const StatusLabel = styled.strong`
  color: #555;
  display: block;
  margin-bottom: 5px;
  flex-shrink: 0;
  text-align: left;
`;

const StatusValue = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
  flex-shrink: 0;
  text-align: left;
`;

const ResponseText = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #007bff;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
  flex-shrink: 0;
`;

const TextInputButton = styled.button<{ $active: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid ${props => props.$active ? '#FFCC00' : 'rgba(255, 255, 255, 0.3)'};
  background: ${props => props.$active ? 'rgba(255, 204, 0, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? '#FFCC00' : '#FFFFFF'};
  font-size: 20px;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 204, 0, 0.3);
    border-color: #FFCC00;
    color: #FFCC00;
    transform: scale(1.1);
  }
`;

const TextInputModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const TextInputContainer = styled.div`
  background: linear-gradient(135deg, #DC143C 0%, #B22222 50%, #8B0000 100%);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 15px;
  padding: 30px;
  width: 90%;
  max-width: 500px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
`;

const TextInputTitle = styled.h3`
  color: #FFFFFF;
  margin: 0 0 20px 0;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
`;

const TextInputField = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: #FFFFFF;
  font-size: 16px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: #FFCC00;
    box-shadow: 0 0 10px rgba(255, 204, 0, 0.3);
  }
`;

const TextInputButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const TextInputCancel = styled.button`
  padding: 12px 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #FFFFFF;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const TextInputSend = styled.button`
  padding: 12px 24px;
  border: 2px solid #FFCC00;
  border-radius: 8px;
  background: rgba(255, 204, 0, 0.2);
  color: #FFCC00;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: rgba(255, 204, 0, 0.3);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [avatarEmotion, setAvatarEmotion] = useState<'idle' | 'listening' | 'thinking' | 'speaking' | 'dancing'>('idle');
  const [currentResponse, setCurrentResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [shouldGreet, setShouldGreet] = useState(true);
  const [isDancing, setIsDancing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState('');
  const avatarRef = useRef<Avatar3DRef>(null);

  // Hook para manejar expiración de sesión
  useAuthExpiration(onLogout);

  // Generar saludo inicial
  useEffect(() => {
    const generateGreeting = async () => {
      if (hasGreeted) return;
      
      const token = AuthService.getToken();
      if (!token) return;

      try {
        setIsProcessing(true);
        setAvatarEmotion('thinking');
        
        const userName = AuthService.getUserDisplayName();
        const greetingPrompt = `Genera un saludo de bienvenida como Greta, asistente virtual de DHL. Saluda específicamente a ${userName} de manera profesional y amigable, preséntate como Greta y menciona que puedes ayudar con servicios de DHL como seguimiento de envíos, cotizaciones, ubicaciones de oficinas, etc. Máximo 3 líneas.`;
        
        const response = await AIService.processQuery(greetingPrompt, token);
        
        if (response.success && response.response) {
          setCurrentResponse(response.response);
          setHasGreeted(true);
          setAvatarEmotion('speaking');
          
          // Generar y reproducir audio del saludo
          const audioUrl = await AIService.textToSpeech(response.response, token);
          if (audioUrl) {
            try {
              const audio = await AIService.playAudio(audioUrl);
              setCurrentAudio(audio);
              
              audio.onended = () => {
                setCurrentAudio(null);
                setAvatarEmotion('idle');
              };
            } catch (error) {
              console.error('Error playing greeting audio:', error);
              setCurrentAudio(null);
              setAvatarEmotion('idle');
            }
          } else {
            setAvatarEmotion('idle');
          }
        }
      } catch (error) {
        console.error('Error generating greeting:', error);
        const userName = AuthService.getUserDisplayName();
        setCurrentResponse(`¡Hola ${userName}! Soy Greta, tu asistente virtual de DHL. Estoy aquí para ayudarte con seguimiento de envíos, cotizaciones y más. ¿En qué puedo asistirte?`);
        setHasGreeted(true);
        setAvatarEmotion('idle');
      } finally {
        setIsProcessing(false);
      }
    };

    generateGreeting();
  }, [hasGreeted]);

  // Controlar saludo inicial del avatar
  useEffect(() => {
    if (shouldGreet) {
      const timer = setTimeout(() => {
        setShouldGreet(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [shouldGreet]);

  const handleVoiceTranscription = async (text: string) => {
    await processUserInput(text);
  };

  // Manejar envío de texto
  const handleTextSubmit = async () => {
    if (!textInput.trim()) return;
    
    const inputText = textInput.trim();
    setTextInput('');
    setShowTextInput(false);
    
    await processUserInput(inputText);
  };

  // Procesar entrada del usuario (voz o texto)
  const processUserInput = async (text: string) => {
    // Verificar si el usuario dijo "baila"
    if (text.toLowerCase().includes('baila')) {
      startDancing();
      return;
    }

    setIsProcessing(true);
    setAvatarEmotion('thinking');
    setCurrentResponse('Procesando tu consulta...');
    
    const token = AuthService.getToken();
    
    if (!token) {
      onLogout();
      return;
    }

    try {
      // Agregar contexto de Greta a la consulta
      const contextualPrompt = `Eres Greta, asistente virtual de DHL. Responde como Greta de manera profesional y amigable. Consulta del usuario: ${text}`;
      
      const response = await AIService.processQuery(contextualPrompt, token);
      
      if (response.success && response.response) {
        setCurrentResponse(response.response);
        setAvatarEmotion('speaking');
        
        // Generar y reproducir audio con Polly (voz Mia)
        const audioUrl = await AIService.textToSpeech(response.response, token);
        
        if (audioUrl) {
          try {
            const audio = await AIService.playAudio(audioUrl);
            setCurrentAudio(audio);
            
            audio.onended = () => {
              setCurrentAudio(null);
              setAvatarEmotion('idle');
            };
          } catch (error) {
            console.error('Error playing audio:', error);
            setCurrentAudio(null);
            setAvatarEmotion('idle');
          }
        } else {
          setAvatarEmotion('idle');
        }
      }
    } catch (error) {
      console.error('Error processing query:', error);
      setCurrentResponse('Lo siento, hubo un error procesando tu consulta. Por favor, intenta de nuevo.');
      setAvatarEmotion('idle');
    } finally {
      setIsProcessing(false);
    }
  };

  const startDancing = () => {
    console.log('💃 Iniciando baile...');
    setIsDancing(true);
    setAvatarEmotion('dancing');
    setCurrentResponse('¡Bailando! 💃 Presiona el micrófono para parar.');
  };

  const stopDancing = () => {
    console.log('🛑 Parando baile...');
    setIsDancing(false);
    setAvatarEmotion('idle');
    setCurrentResponse('¡Hola! Soy Greta, tu asistente virtual de DHL. ¿En qué puedo ayudarte?');
  };

  const handleMicStart = () => {
    // Si está bailando, parar el baile
    if (isDancing) {
      stopDancing();
      return;
    }
    
    setIsListening(true);
    setAvatarEmotion('listening');
    setCurrentResponse('Te escucho... Puedes preguntarme sobre servicios de DHL.');
  };

  const handleMicStop = () => {
    setIsListening(false);
    if (!isProcessing) {
      setAvatarEmotion('idle');
    }
  };

  const handleStopSpeaking = () => {
    AIService.stopAudio();
    setCurrentAudio(null);
    setAvatarEmotion('idle');
  };

  const getStatusText = () => {
    if (isProcessing) return 'Procesando...';
    if (avatarEmotion === 'speaking') return 'Greta hablando';
    if (avatarEmotion === 'listening') return 'Greta escuchando';
    if (avatarEmotion === 'thinking') return 'Greta pensando';
    if (avatarEmotion === 'dancing') return 'Greta bailando';
    return 'Greta lista';
  };

  return (
    <DashboardContainer>
      <Navbar onLogout={onLogout} />
      <MainContent>
        {/* Panel izquierdo - Respuestas de IA */}
        <LeftPanel>
          {currentResponse && (
            <StatusItem>
              <StatusLabel>Greta</StatusLabel>
              <ResponseText>{currentResponse}</ResponseText>
            </StatusItem>
          )}
        </LeftPanel>

        {/* Centro - Avatar 3D */}
        <CenterPanel>
          <Avatar3D 
            ref={avatarRef}
            modelPath="/models/Greta.glb" 
            emotion={avatarEmotion}
            isAnimating={avatarEmotion === 'speaking'}
            shouldGreet={shouldGreet}
            isDancing={isDancing}
            audioElement={currentAudio}
          />
        </CenterPanel>

        {/* Panel derecho - Controles de voz */}
        <RightPanel>
          <VoiceRecorder
            onTranscription={handleVoiceTranscription}
            onStopSpeaking={handleStopSpeaking}
            onMicStart={handleMicStart}
            onMicStop={handleMicStop}
            isAISpeaking={avatarEmotion === 'speaking'}
            isProcessing={isProcessing}
            isListening={isListening}
          />
          
          {/* Botón para mostrar entrada de texto */}
          <TextInputButton 
            onClick={() => setShowTextInput(!showTextInput)}
            $active={showTextInput}
          >
            ⌨️
          </TextInputButton>
        </RightPanel>
      </MainContent>

      {/* Modal de entrada de texto */}
      <AnimatePresence>
        {showTextInput && (
          <TextInputModal
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            <TextInputContainer>
              <TextInputTitle>Escribe tu consulta para Greta</TextInputTitle>
              <TextInputField
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Escribe aquí tu pregunta sobre DHL..."
                onKeyPress={(e) => e.key === 'Enter' && handleTextSubmit()}
                autoFocus
              />
              <TextInputButtons>
                <TextInputCancel onClick={() => setShowTextInput(false)}>
                  Cancelar
                </TextInputCancel>
                <TextInputSend onClick={handleTextSubmit} disabled={!textInput.trim()}>
                  Enviar
                </TextInputSend>
              </TextInputButtons>
            </TextInputContainer>
          </TextInputModal>
        )}
      </AnimatePresence>
    </DashboardContainer>
  );
};

export default Dashboard;
